// Implements: F-USG-006
/**
 * What a version cost, as text (mockup v3 P7, v3 `verCost`): "USD 0.12 · 3 min" in the chip,
 * the timeline and "Detalles"; "USD 0.12" in a table; "Sin coste · restaurada" (chip) or
 * "Sin coste" (table) for a version no run produced (a restored one, owner decision v3).
 *
 * The amount has exactly two decimals, rounded half up from the integer micros, in the
 * platform locale (`es-PE`, decimal point: `types/locale.ts`), whatever the browser's. A
 * currency code `Intl` does not know is still written as code and amount. A duration is
 * whole minutes, rounded, never under one. Shared by the modules that show a cost, which
 * may not reach into one another.
 */

import { t } from "../i18n";
import type { CostSummary } from "../../types/api";
import type { VersionCostItem } from "../../types/costs";
import { PLATFORM_LOCALE } from "../../types/locale";

export type CostLabelMode = "chip" | "table";

const MICROS_PER_UNIT = 1_000_000;
const MS_PER_MINUTE = 60_000;

const AMOUNT = new Intl.NumberFormat(PLATFORM_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function costAmountText(cost: CostSummary): string {
  return t("cost.amount", { currency: cost.currency, amount: AMOUNT.format(cost.amount_micros / MICROS_PER_UNIT) });
}

export function durationText(durationMs: number): string {
  return t("cost.duration", { minutes: Math.max(1, Math.round(durationMs / MS_PER_MINUTE)) });
}

/** The label of one version's cost; `null` while it is not known (nothing is drawn then). */
export function costLabel(item: VersionCostItem | undefined, mode: CostLabelMode): string | null {
  if (item === undefined) return null;
  if (item.run === null) return mode === "chip" ? t("cost.noneRestored") : t("cost.none");
  const amount = costAmountText(item.run.cost);
  return mode === "chip" ? `${amount} · ${durationText(item.run.duration_ms)}` : amount;
}
